import { useCallback } from 'react'

import { useEventCapture } from '@surglogs-app/analytics'
import { useAuthTokenRevokePostMutation } from '@surglogs-app/network'
import {
  getAuditsInactivityRoute,
  getAuthInactivityRoute,
  getStandaloneLoginRoute,
  redirectToExternalUrl,
  useLocationToVisitStorage,
} from '@surglogs-app/routing'

import { useHandler } from '../../../../hooks/src'
import { useAuthToken } from '../../hooks'

const getStandaloneAuthRedirectUrl = ({
  redirectType,
  referrer,
  organization,
}: {
  referrer?: string
  redirectType?: 'login' | 'inactivity'
  organization?: string
}) => {
  if (redirectType === 'inactivity') {
    if (referrer === 'audits') {
      return getAuditsInactivityRoute({ referrer, organization })
    }

    return getAuthInactivityRoute({ referrer })
  }

  if (referrer === 'external') {
    return getAuthInactivityRoute({ referrer })
  }

  return getStandaloneLoginRoute({ referrer, organization })
}

export const useInactivityRedirect = ({
  referrer,
  organization,
}: {
  referrer?: string
  organization?: string
}) => {
  const { token, refreshToken, clearAuthData } = useAuthToken()
  const { mutateAsync: revokeJwtTokenMutateAsync } =
    useAuthTokenRevokePostMutation()
  const { setValue } = useLocationToVisitStorage()
  const { captureEvent } = useEventCapture()

  const logoutHandler = useHandler(
    async (logoutMutateAsync: () => Promise<void>, redirectUrl: string) => {
      try {
        setValue(window.location)
        if (token) {
          await logoutMutateAsync()
          clearAuthData()
        }
      } finally {
        // we move on even when revoke failed because
        // we cleaned tokens from the storage  anyway
        captureEvent('session_expired', { referrer })
        redirectToExternalUrl(redirectUrl, { replace: true })
      }
    },
  )

  const cleanUpAndRedirect = useCallback(
    async (redirectType: 'login' | 'inactivity') => {
      await logoutHandler(
        () =>
          revokeJwtTokenMutateAsync({
            access_token: token,
            refresh_token: refreshToken,
          }),
        getStandaloneAuthRedirectUrl({
          redirectType,
          referrer,
          organization,
        }),
      )
    },
    [
      logoutHandler,
      referrer,
      organization,
      revokeJwtTokenMutateAsync,
      token,
      refreshToken,
    ],
  )

  return {
    cleanUpAndRedirect,
  }
}
